import React, { FC, useEffect, useRef, useState } from "react";
import { Box, Button, Notification, Select, Text, TextField } from "@telia/teddy";
import { API_URL_SEND_FEEDBACK } from "../../../api";
import { retailerFetch } from "../../../retailerFetch";
import "./SendFeedbackPanel.less";

interface Props {
    onOpenChange: (open: boolean) => void;
    open: boolean;
}

const MAX_DESCRIPTION_LENGTH = 500;
const FEEDBACK_TYPE_VALUES = ["Generell tilbakemelding", "Bug", "Forslag til forbedring"] as const;
const FEEDBACK_TYPE_OPTIONS = [...FEEDBACK_TYPE_VALUES];
type FeedbackType = (typeof FEEDBACK_TYPE_VALUES)[number];

const SendFeedbackPanel: FC<Props> = ({ open, onOpenChange }) => {
    const [description, setDescription] = useState("");
    const [feedbackType, setFeedbackType] = useState<FeedbackType>(FEEDBACK_TYPE_VALUES[0]);
    const [submitState, setSubmitState] = useState<"idle" | "sending" | "success" | "error">("idle");
    const [errorMessage, setErrorMessage] = useState("");

    const handleSubmit = async () => {
        const sanitizedDescription = description.trim().slice(0, MAX_DESCRIPTION_LENGTH);
        if (!sanitizedDescription) return;

        setSubmitState("sending");
        setErrorMessage("");

        try {
            const response = await retailerFetch(API_URL_SEND_FEEDBACK, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    description: sanitizedDescription,
                    feedbackType,
                }),
            });

            if (!response.ok) {
                let serverError = "";
                try {
                    const body = await response.json();
                    if (body && typeof body.error === "string") {
                        serverError = body.error;
                    }
                } catch {
                    // non-JSON response, fall through to default message
                }
                throw new Error(serverError || "Kunne ikke sende tilbakemelding. Prøv igjen. [0]");
            }

            setSubmitState("success");
            setDescription("");
        } catch (err) {
            setErrorMessage(err instanceof Error ? err.message : "Kunne ikke sende tilbakemelding. Prøv igjen. [1]");
            setSubmitState("error");
        }
    };

    const dialogRef = useRef<HTMLDivElement | null>(null);
    const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (open) {
            setSubmitState("idle");
            setFeedbackType(FEEDBACK_TYPE_VALUES[0]);
            const activeElement = document.activeElement;
            if (activeElement instanceof HTMLElement) {
                previouslyFocusedElementRef.current = activeElement;
            }
            if (dialogRef.current) {
                dialogRef.current.focus();
            }
        } else if (previouslyFocusedElementRef.current) {
            const elementToFocus = previouslyFocusedElementRef.current;
            if (document.contains(elementToFocus)) {
                elementToFocus.focus();
            }
            previouslyFocusedElementRef.current = null;
        }
    }, [open]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "Escape") {
            event.stopPropagation();
            onOpenChange(false);
            return;
        }

        if (event.key === "Tab" && dialogRef.current) {
            const focusableSelectors =
                'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
            const focusableElements = Array.from(
                dialogRef.current.querySelectorAll<HTMLElement>(focusableSelectors),
            ).filter((el) => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true");

            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            const currentIndex = focusableElements.indexOf(document.activeElement as HTMLElement);

            if (event.shiftKey) {
                if (document.activeElement === firstElement || currentIndex === -1) {
                    event.preventDefault();
                    lastElement.focus();
                }
            } else {
                if (document.activeElement === lastElement || currentIndex === -1) {
                    event.preventDefault();
                    firstElement.focus();
                }
            }
        }
    };

    if (!open) return null;

    return (
        <div
            className="report-error-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="report-error-panel-title"
            ref={dialogRef}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
        >
            <div className="report-error-panel__header">
                <Text id="report-error-panel-title" variant="paragraph-100" as="span" style={{ fontWeight: 600 }}>
                    Gi tilbakemelding
                </Text>
                <button
                    className="report-error-panel__close"
                    onClick={() => onOpenChange(false)}
                    aria-label="lukk"
                    type="button"
                >
                    ✕
                </button>
            </div>
            <div className="report-error-panel__body">
                <Text mb="200">Hjelp oss å forbedre Nexus! Vi setter pris på alle tilbakemeldinger.</Text>
                {submitState === "success" && (
                    <Notification type="tile" variant="success" mb="200">
                        <Notification.Icon />
                        <Notification.Text>Tilbakemeldingen ble sendt. Takk!</Notification.Text>
                    </Notification>
                )}
                {submitState === "error" && (
                    <Notification type="tile" variant="warning" mb="200">
                        <Notification.Icon />
                        <Notification.Text>{errorMessage}</Notification.Text>
                    </Notification>
                )}
                <Box mb="300">
                    <Select
                        className="report-error-panel__select"
                        name="feedbackType"
                        options={FEEDBACK_TYPE_OPTIONS}
                        value={feedbackType}
                        onValueChange={(value) => {
                            setFeedbackType(value as FeedbackType);
                            return value;
                        }}
                    >
                        <Select.Trigger>
                            <Select.Value placeholder="Velg type" />
                        </Select.Trigger>
                        <Select.Content>
                            <Select.Viewport>
                                {FEEDBACK_TYPE_VALUES.map((option) => (
                                    <Select.Item key={option} value={option}>
                                        {option}
                                    </Select.Item>
                                ))}
                            </Select.Viewport>
                        </Select.Content>
                    </Select>
                </Box>
                <Box mb="300">
                    <TextField>
                        <TextField.Label>Din tilbakemelding</TextField.Label>
                        <TextField.Textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
                            maxLength={MAX_DESCRIPTION_LENGTH}
                            rows={10}
                        />
                    </TextField>
                    <Text variant="additional-100" style={{ textAlign: "right", marginTop: "0.25rem" }}>
                        {description.length}/{MAX_DESCRIPTION_LENGTH}
                    </Text>
                </Box>
                <Button
                    variant="primary"
                    fullWidth
                    onClick={handleSubmit}
                    disabled={!description.trim() || submitState === "sending"}
                >
                    {submitState === "sending" ? "Sender..." : "Send tilbakemelding"}
                </Button>
            </div>
        </div>
    );
};

export default SendFeedbackPanel;
